import React, { useState, useEffect } from 'react';
import { FaUsers, FaCrown, FaClock, FaTrophy, FaSortAmountDown } from 'react-icons/fa';
import { RefreshCw, X } from 'lucide-react';

/* ================= TYPES ================= */

interface Seller {
  id: number;
  companyName: string;
  totalDurationMinutes: number;
  priority: number; // 0 = Auto, 1+ = Manual
}

const BestDealerPage: React.FC = () => {
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Popup States
  const [showModal, setShowModal] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState<Seller | null>(null);
  const [newPriority, setNewPriority] = useState(0);

  // --- 1. FETCH DATA ---
  const fetchSellers = async () => {
    setLoading(true);
    try {
      const res = await fetch('/springboot/api/admin/sellers-activity');
      if (res.ok) {
        const data = await res.json();
        setSellers(data);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSellers(); }, []);

  // --- 2. UPDATE PRIORITY (API Call) ---
  const savePriority = async () => {
    if (!selectedSeller) return;

    try {
      await fetch(`/springboot/api/admin/set-priority/${selectedSeller.id}?priority=${newPriority}`, {
        method: 'POST'
      });
      
      setShowModal(false);
      fetchSellers(); 
    } catch (e) {
      alert("Failed to update priority");
    }
  };

  const openPopup = (seller: Seller) => {
    setSelectedSeller(seller);
    setNewPriority(seller.priority || 0);
    setShowModal(true);
  };

  // --- 3. SORTING LOGIC (Same as before) ---
  const sortedSellers = [...sellers].sort((a, b) => {
    const pA = a.priority || 0;
    const pB = b.priority || 0;

    if (pA > 0 && pB > 0) return pA - pB;
    if (pA > 0) return -1;
    if (pB > 0) return 1;

    return b.totalDurationMinutes - a.totalDurationMinutes;
  });

  const top5Sellers = sortedSellers.slice(0, 5);

  const formatDuration = (mins: number) => {
     if(!mins) return "0m";
     const h = Math.floor(mins/60);
     const m = mins%60;
     return h>0 ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <div className="min-h-screen bg-gray-50/50 pb-20 font-sans animate-fade-in-up">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* HEADER Section */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2">
              <FaTrophy className="text-[#006d5b]" /> Top Performing Dealers
            </h1>
            <p className="text-gray-500 text-xs font-bold mt-1 ml-1">
              Rankings based on active engagement & priority settings
            </p>
          </div>
          <div className="flex gap-3 w-full md:w-auto">
             <div className="hidden md:flex items-center gap-2 bg-[#006d5b]/5 px-5 py-2.5 rounded-xl text-[#006d5b] font-bold text-xs border border-[#006d5b]/10">
                <FaUsers /> Total Sellers: {sellers.length}
             </div>
             <button 
               onClick={fetchSellers} 
               className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-gray-900 hover:bg-[#006d5b] text-white px-5 py-2.5 rounded-xl shadow-lg shadow-gray-200 transition-all font-bold text-xs"
             >
                <RefreshCw size={14} className={loading ? "animate-spin":""} /> Refresh List
             </button>
          </div>
        </div>

        {/* TOP 5 WINNERS GRID */}
        <div>
           <h3 className="text-sm font-black text-gray-800 uppercase tracking-widest mb-4 flex items-center gap-2">
             <FaCrown className="text-yellow-500" /> Leaderboard
           </h3>
           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
             {top5Sellers.map((seller, index) => (
               <div key={seller.id} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 relative overflow-hidden group hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                 
                 {/* Rank Badge */}
                 <div className={`absolute top-0 right-0 px-3 py-1 rounded-bl-xl text-[10px] font-black tracking-wider text-white ${index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-400' : 'bg-[#006d5b]'}`}>
                    RANK #{index + 1}
                 </div>

                 {/* Manual Priority Badge */}
                 {seller.priority > 0 && (
                     <div className="absolute top-0 left-0 bg-[#006d5b]/10 text-[#006d5b] px-2 py-1 rounded-br-lg text-[9px] font-black flex items-center gap-1">
                         📌 PINNED
                     </div>
                 )}

                 <div className="flex flex-col items-center mt-4">
                   <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-black shadow-md mb-3 bg-gradient-to-tr ${index === 0 ? 'from-yellow-400 to-orange-500' : 'from-[#006d5b] to-[#4db6ac]'}`}>
                       {seller.companyName?.charAt(0).toUpperCase()}
                   </div>
                   
                   <h3 className="font-bold text-gray-900 text-sm truncate w-full text-center px-2" title={seller.companyName}>
                     {seller.companyName}
                   </h3>
                   
                   <div className="mt-3 bg-gray-50 rounded-lg px-3 py-1.5 flex items-center gap-2 text-xs font-bold text-gray-600">
                      <FaClock className="text-[#006d5b]" /> {formatDuration(seller.totalDurationMinutes)}
                   </div>
                 </div>
               </div>
             ))}
           </div>
        </div>

        {/* FULL LIST */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
           <div className="p-6 border-b border-gray-50 flex justify-between items-center bg-gray-50/30">
              <h3 className="text-sm font-black text-gray-800 uppercase tracking-widest flex items-center gap-2">
                <FaSortAmountDown /> All Sellers Activity
              </h3>
              <span className="text-xs font-bold text-gray-400">Sorted by Rank</span>
           </div>
           
           <div className="divide-y divide-gray-50">
             {sortedSellers.map((seller, index) => (
               <div
                 key={seller.id}
                 className={`px-6 py-4 flex flex-col md:flex-row justify-between items-center gap-4 transition-colors hover:bg-gray-50/50 ${
                     seller.priority > 0 ? 'bg-[#006d5b]/5' : ''
                 }`}
               >
                 {/* Left Info */}
                 <div className="flex items-center gap-4 w-full md:w-auto">
                   <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 font-black text-xs border border-gray-200 uppercase">
                       {seller.companyName?.charAt(0)}
                   </div>
                   <div>
                     <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                         {seller.companyName}
                         {seller.priority > 0 && <FaCrown className="text-yellow-500 text-[10px]"/>}
                     </h4>
                     <p className="text-xs text-gray-400 font-medium flex items-center gap-2 mt-0.5">
                       <span className="flex items-center gap-1"><FaClock size={10}/> {formatDuration(seller.totalDurationMinutes)}</span>
                       {seller.priority > 0 && (
                         <span className="text-[#006d5b] font-bold bg-white px-1.5 rounded border border-[#006d5b]/20 text-[9px] uppercase">
                           Priority Set: {seller.priority}
                         </span>
                       )}
                     </p>
                   </div>
                 </div>

                 {/* Right Actions */}
                 <div className="flex items-center justify-between w-full md:w-auto gap-6">
                     <div className="text-right hidden md:block">
                        <span className="block text-[10px] font-black text-gray-300 uppercase">Current Rank</span>
                        <span className="text-lg font-black text-gray-900">#{index + 1}</span>
                     </div>
                     <button
                       onClick={() => openPopup(seller)}
                       className="px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg text-xs font-bold hover:bg-[#006d5b] hover:text-white hover:border-[#006d5b] transition-all shadow-sm w-full md:w-auto"
                     >
                       Change Priority
                     </button>
                 </div>
               </div>
             ))}
           </div>
        </div>

        {/* MODERN MODAL */}
        {showModal && selectedSeller && (
          <div className="fixed inset-0 bg-[#001e1d]/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-4">
            <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl relative overflow-hidden">
              {/* Close Button */}
              <button 
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors"
              >
                <X size={20} />
              </button>

              <div className="text-center mb-6">
                 <div className="w-16 h-16 bg-[#006d5b]/10 text-[#006d5b] rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                    <FaCrown />
                 </div>
                 <h3 className="text-xl font-black text-gray-900">Set Rank Priority</h3>
                 <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                    Override the automatic ranking for <strong className="text-gray-900">{selectedSeller.companyName}</strong>. 
                    <br/>Lower number = Higher Rank.
                 </p>
              </div>

              <div className="space-y-4">
                 <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Select Position</label>
                    <div className="relative">
                      <select
                        value={newPriority}
                        onChange={e => setNewPriority(Number(e.target.value))}
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#006d5b]/20 focus:border-[#006d5b] appearance-none cursor-pointer"
                      >
                        <option value={0}>Auto (Based on Activity)</option>
                        <option value={1}>Rank #1 (Highest Priority)</option>
                        <option value={2}>Rank #2</option>
                        <option value={3}>Rank #3</option>
                        <option value={4}>Rank #4</option>
                        <option value={5}>Rank #5</option>
                        <option value={10}>Rank #10</option>
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                        <FaSortAmountDown />
                      </div>
                    </div>
                 </div>

                 <button
                    onClick={savePriority}
                    className="w-full bg-[#006d5b] text-white py-3.5 rounded-xl font-bold text-sm shadow-lg shadow-[#006d5b]/20 hover:bg-[#005a4b] hover:shadow-xl transition-all"
                  >
                    Save & Update Rank
                  </button>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-50 text-center">
                 <p className="text-[10px] text-gray-400 font-bold">
                    Changes will reflect immediately on the leaderboard.
                 </p>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default BestDealerPage;